import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, CopyPlus, Trash2, Upload, Plus } from "lucide-react";
import { cloner, rawUrl } from "@/lib/clonerApi";
import { apiError } from "@/lib/userApi";

const INSERTS = [
  ["Título", "<h2>Nuevo título</h2>"],
  ["Párrafo", "<p>Texto nuevo de ejemplo.</p>"],
  ["Botón", '<a href="#" style="display:inline-block;padding:12px 22px;border-radius:999px;background:#C2620E;color:#fff;text-decoration:none">Botón</a>'],
  ["Imagen", '<img src="https://placehold.co/600x300" alt="Imagen" style="max-width:100%" />'],
  ["Sección", '<section style="padding:48px 24px"><h2>Sección nueva</h2><p>Contenido.</p></section>'],
];

/** Contenido del elemento: texto, enlace, imagen, atributos y acciones. */
export const ContentPanel = ({ el, siteId, path, onChange }) => {
  const fileRef = useRef(null);
  const [attr, setAttr] = useState({ name: "", value: "" });
  if (!el) return <p className="text-xs text-white/40 p-4">Toca un elemento de la página.</p>;

  const tag = el.tagName.toLowerCase();
  const touch = () => onChange();

  const upload = async (file) => {
    const dir = (path || "").split("/").slice(0, -1).join("/");
    const dest = `${dir ? dir + "/" : ""}subidas/${Date.now()}_${file.name.replace(/[^\w.-]/g, "_")}`;
    try {
      await cloner.upload(siteId, dest, file);
      el.setAttribute("src", rawUrl(siteId, dest));
      touch();
      toast.success("Imagen cambiada");
    } catch (e) { toast.error(apiError(e, "No se pudo subir la imagen")); }
  };

  const move = (dir) => {
    const sib = dir < 0 ? el.previousElementSibling : el.nextElementSibling;
    if (!sib) return;
    dir < 0 ? sib.before(el) : sib.after(el);
    touch();
  };

  return (
    <div className="p-3 space-y-3" data-testid="content-panel">
      <div className="text-[10px] text-white/40 uppercase tracking-wide">
        Elemento &lt;{tag}&gt;
      </div>

      {el.children.length === 0 && (
        <textarea data-testid="content-text" rows={3} value={el.textContent}
          onChange={(e) => { el.textContent = e.target.value; touch(); }}
          className="w-full glass-card px-2 py-1.5 bg-black/40 text-white text-[11px] outline-none" />
      )}

      {tag === "a" && (
        <input data-testid="content-href" value={el.getAttribute("href") || ""}
          onChange={(e) => { el.setAttribute("href", e.target.value); touch(); }}
          placeholder="Enlace (href)"
          className="w-full glass-card px-2 py-1.5 bg-black/40 text-white text-[11px] font-mono outline-none" />
      )}

      {tag === "img" && (
        <>
          <input data-testid="content-src" value={el.getAttribute("src") || ""}
            onChange={(e) => { el.setAttribute("src", e.target.value); touch(); }}
            placeholder="Dirección de la imagen"
            className="w-full glass-card px-2 py-1.5 bg-black/40 text-white text-[11px] font-mono outline-none" />
          <button onClick={() => fileRef.current?.click()} data-testid="content-upload"
            className="glass-btn px-3 py-1.5 rounded-lg text-[11px] flex items-center gap-1.5">
            <Upload className="w-3 h-3" /> Subir desde mi equipo
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </>
      )}

      <div>
        <div className="text-[10px] text-white/40 uppercase tracking-wide mb-1">Atributos</div>
        {Array.from(el.attributes).filter((a) => !a.name.startsWith("data-fx")).map((a) => (
          <div key={a.name} className="flex items-center gap-1.5 mb-1">
            <span className="text-[10px] text-white/50 w-16 truncate shrink-0">{a.name}</span>
            <input value={a.value} data-testid={`attr-${a.name}`}
              onChange={(e) => { el.setAttribute(a.name, e.target.value); touch(); }}
              className="flex-1 min-w-0 glass-card px-2 py-1 bg-black/40 text-white text-[10px] font-mono outline-none" />
          </div>
        ))}
        <div className="flex items-center gap-1.5 mt-1.5">
          <input value={attr.name} onChange={(e) => setAttr({ ...attr, name: e.target.value })}
            placeholder="nombre" data-testid="attr-new-name"
            className="w-20 glass-card px-2 py-1 bg-black/40 text-white text-[10px] outline-none" />
          <input value={attr.value} onChange={(e) => setAttr({ ...attr, value: e.target.value })}
            placeholder="valor" data-testid="attr-new-value"
            className="flex-1 min-w-0 glass-card px-2 py-1 bg-black/40 text-white text-[10px] outline-none" />
          <button data-testid="attr-add" className="glass-btn px-2 py-1 rounded text-[10px]"
            onClick={() => {
              if (!attr.name) return;
              el.setAttribute(attr.name, attr.value);
              setAttr({ name: "", value: "" });
              touch();
            }}><Plus className="w-3 h-3" /></button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-white/8">
        <button onClick={() => move(-1)} data-testid="el-up"
          className="glass-btn px-2 py-1.5 rounded text-[10px] flex items-center gap-1">
          <ArrowUp className="w-3 h-3" /> Subir</button>
        <button onClick={() => move(1)} data-testid="el-down"
          className="glass-btn px-2 py-1.5 rounded text-[10px] flex items-center gap-1">
          <ArrowDown className="w-3 h-3" /> Bajar</button>
        <button data-testid="el-duplicate"
          onClick={() => { el.after(el.cloneNode(true)); touch(); }}
          className="glass-btn px-2 py-1.5 rounded text-[10px] flex items-center gap-1">
          <CopyPlus className="w-3 h-3" /> Duplicar</button>
        <button data-testid="el-delete"
          onClick={() => { el.remove(); touch(true); }}
          className="glass-btn px-2 py-1.5 rounded text-[10px] flex items-center gap-1 text-rose-300">
          <Trash2 className="w-3 h-3" /> Borrar</button>
      </div>

      <div>
        <div className="text-[10px] text-white/40 uppercase tracking-wide mb-1">Insertar debajo</div>
        <div className="flex flex-wrap gap-1.5">
          {INSERTS.map(([label, html]) => (
            <button key={label} data-testid={`insert-${label}`}
              onClick={() => { el.insertAdjacentHTML("afterend", html); touch(); }}
              className="matrix-chip px-2 py-1 rounded text-[10px]">{label}</button>
          ))}
        </div>
      </div>
    </div>
  );
};
